/**
 * W5 (CU-14zefhy38pt, twin of RouteMagic CU-14zefhy29mu X3): with more chats than fit the tab strip, the tab that waits
 * for a screen answer can be scrolled out of view. The panel scrolls the opened tab into view and pins a "!" for a hidden
 * asking tab. Pure helpers, unit tested without the component.
 */

/** A chat other than the one on screen with a screen request the user has not answered. */
export function needsScreenAnswer(
    session: {key: string; messages?: Array<{screen?: {state?: string}}>} | null | undefined,
    activeKey: string
): boolean {
    return !!session && session.key !== activeKey && (session.messages || []).some((m) => m.screen?.state === 'asking');
}

/** The key of the first chat that waits for a screen answer, or null. */
export function screenAskKey(
    sessions: Array<{key: string; messages?: Array<{screen?: {state?: string}}>}> | null | undefined,
    activeKey: string
): string | null {
    const asking = (sessions || []).find((s) => needsScreenAnswer(s, activeKey));
    return asking ? asking.key : null;
}

/** The key that makes the hidden-ask check run again: tab count plus the asking tab (a closed tab to its left moves it). */
export function askRecheckKey(tabCount: number, askKey: string | null): string {
    return `${tabCount}|${askKey}`;
}

/** Runs `run` once (next tick) each time the check key changes; cancel() drops a pending run (component destroy). */
export function createAskRecheck(
    run: () => void,
    timers: {set: (fn: () => void) => any; clear: (id: any) => void} = {set: (fn) => setTimeout(fn, 0), clear: (id) => clearTimeout(id)}
): {check: (key: string) => void; cancel: () => void} {
    let seen: string | null = null;
    let timer: any;
    return {
        check: (key: string) => {
            if (key !== seen) {
                seen = key;
                timers.clear(timer);
                timer = timers.set(run);
            }
        },
        cancel: () => timers.clear(timer)
    };
}

/** Keeps one resize observer on the element that exists now: attach() drops the old one first, detach() drops all. */
export function createStripWatcher<T>(factory: (target: T) => {disconnect: () => void} | undefined): {
    attach: (el: T | undefined) => void;
    detach: () => void;
} {
    let current: {disconnect: () => void} | undefined;
    const detach = () => {
        current?.disconnect();
        current = undefined;
    };
    return {
        attach: (el) => {
            detach();
            if (el) {
                current = factory(el);
            }
        },
        detach
    };
}

/**
 * The scrollLeft of a horizontal strip that shows the whole item (the nearest edge; unchanged when it is already in view).
 * Item left is measured from the start of the strip's content.
 */
export function revealScrollLeft(view: {scrollLeft: number; width: number}, item: {left: number; width: number}): number {
    const start = Math.max(0, view.scrollLeft || 0);
    const width = Math.max(0, view.width || 0);
    // 1 px slack: sub-pixel layout must not count a visible tab as hidden
    if (item.left < start - 1) {
        return Math.max(0, item.left);
    }
    if (item.left + item.width > start + width + 1) {
        return Math.max(0, Math.min(item.left, item.left + item.width - width));
    }
    return start;
}
